import React, {useEffect, useState} from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Card,
    CardMedia,
    CardContent, Dialog, DialogContent, CircularProgress
} from '@mui/material';
import {addMessage, getWorkspaceSettings, uploadImage, checkMessageContent} from "./firebaseService";
import {useParams} from "react-router-dom";

const Guestbook = () => {
    const { workspaceId } = useParams(); // URL에서 workspaceId 가져오기

    const [message, setMessage] = useState('');
    const [name, setName] = useState('');
    const [affiliation, setAffiliation] = useState('');
    const [position, setPosition] = useState('');
    const [phone, setPhone] = useState(''); // 전화번호 상태 추가
    const [isAgreed, setIsAgreed] = useState(false);
    const [photo, setPhoto] = useState(null); // 파일 객체
    const [photoPreview, setPhotoPreview] = useState(null); // 이미지 미리보기 URL
    const [isDialogOpen, setIsDialogOpen] = useState(false); // 개인정보 동의 팝업 상태
    const [isMessageRejectDialogOpen, setIsMessageRejectDialogOpen] = useState(false);
    const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [rejectReason, setRejectReason] = useState({ explanation: '' });
    const [workspaceSettings, setWorkspaceSettings] = useState({
        backgroundImage: '',
        logo: '',
        isMessageBoardOpen : false,
        guestbookBackgroundImage: '',
        privacyAgreement: '',
        noticeMessage: '',
    }); // 워크스페이스 설정 상태

    const [loading, setLoading] = useState(false); // 로딩 상태 관리
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

    useEffect(() => {
        // 워크스페이스 설정 가져오기
        const fetchSettings = async () => {
            const settings = await getWorkspaceSettings(workspaceId);
            setWorkspaceSettings(settings);
        };
        fetchSettings();
    }, [workspaceId]);

    const handleDialogOpen = () => {
        setIsDialogOpen(true);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setPhoto(file);

        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleMessageRejectDialogOpen = (explanation) => {
        setRejectReason({ explanation: explanation || '부적절한 내용이 포함되어 있습니다.' });
        setIsMessageRejectDialogOpen(true);
    };

    const handleMessageRejectDialogClose = () => {
        setIsMessageRejectDialogOpen(false);
        setRejectReason({ explanation: '' });
    };

    const handleAlertDialogOpen = (message) => {
        setAlertMessage(message);
        setIsAlertDialogOpen(true);
    };

    const handleAlertDialogClose = () => {
        setIsAlertDialogOpen(false);
        setAlertMessage('');
    };

    const handleSaveClick = () => {
        setIsConfirmDialogOpen(true);
    };

    const handleConfirm = async () => {
        setIsConfirmDialogOpen(false);
        await handleSubmit();
    };

    const handleCancel = () => {
        setIsConfirmDialogOpen(false);
    };

    // 전화번호 자동 포맷팅 함수
    const formatPhoneNumber = (value) => {
        // 숫자만 추출
        const numbers = value.replace(/[^\d]/g, '');
        
        // 최대 11자리까지만 허용
        const limitedNumbers = numbers.slice(0, 11);
        
        // 포맷팅
        if (limitedNumbers.length <= 3) {
            return limitedNumbers;
        } else if (limitedNumbers.length <= 7) {
            return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3)}`;
        } else if (limitedNumbers.length <= 10) {
            // 02 같은 경우
            if (limitedNumbers.startsWith('02')) {
                return `${limitedNumbers.slice(0, 2)}-${limitedNumbers.slice(2, 6)}-${limitedNumbers.slice(6)}`;
            } else {
                return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3, 6)}-${limitedNumbers.slice(6)}`;
            }
        } else {
            // 11자리 (010-0000-0000 형식)
            if (limitedNumbers.startsWith('02')) {
                return `${limitedNumbers.slice(0, 2)}-${limitedNumbers.slice(2, 6)}-${limitedNumbers.slice(6, 10)}`;
            } else {
                return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3, 7)}-${limitedNumbers.slice(7, 11)}`;
            }
        }
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        setPhone(formatted);
    };

    const handleSubmit = async () => {
        if (!message.trim() || !name.trim()) {
            handleAlertDialogOpen('메시지와 이름을 입력해주세요.');
            return;
        }

        // 전화번호 유효성 검사 (선택사항이므로 입력된 경우에만 검사)
        if (phone && !/^[0-9]{2,3}-[0-9]{3,4}-[0-9]{4}$/.test(phone)) {
            handleAlertDialogOpen('전화번호 형식이 올바르지 않습니다. (예: 010-1234-5678)');
            return;
        }

        try {
            setLoading(true);
            
            // 2. API 서비스를 통한 비속어 필터 확인
            const checkResult = await checkMessageContent(message);

            // 3.2 문제가 있다면 팝업 띄워서 문장 수정하도록 알려주기
            if (checkResult.is_toxic === true) {
                handleMessageRejectDialogOpen(checkResult.explanation);
                return;
            }

            // 3.1 문제가 없는 문장이라면 그대로 등록
            let photoURL = null;

            if (photo) {
                photoURL = await uploadImage(photo, `workspaces/${workspaceId}/photos`);
            }

            const newMessage = {
                message,
                name,
                affiliation,
                position,
                phone,
                image: photoURL,
                timestamp: new Date(),
                status: 'pending', // 승인 대기 상태로 저장
            };

            await addMessage(newMessage, workspaceId);

            setMessage('');
            setName('');
            setAffiliation('');
            setPosition('');
            setPhone('');
            setPhoto(null);
            setPhotoPreview(null);
            handleAlertDialogOpen('메시지가 저장되었습니다!');
        } catch (error) {
            console.error('Error saving message:', error);
            handleAlertDialogOpen('메시지 저장 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                width: '100vw',
                minHeight: '100vh',
                backgroundImage: `url(${workspaceSettings.guestbookBackgroundImage || workspaceSettings.backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                position: 'relative',
                padding: 0,
                margin: 0,
            }}
        >
            {/* 좌측 상단 로고 */}
            {workspaceSettings.leftLogo && (
                <Box
                    component="img"
                    src={workspaceSettings.leftLogo}
                    alt="좌측 상단 로고"
                    sx={{
                        position: 'absolute',
                        top: '10px',
                        left: '20px',
                        width: '150px',
                        height: '100px',
                        objectFit: 'contain',
                        zIndex: 1,
                    }}
                />
            )}

            {/* 우측 상단 로고 */}
            {workspaceSettings.rightLogo && (
                <Box
                    component="img"
                    src={workspaceSettings.rightLogo}
                    alt="우측 상단 로고"
                    sx={{
                        position: 'absolute',
                        top: '10px',
                        right: '20px',
                        width: '150px',
                        height: '100px',
                        objectFit: 'contain',
                        zIndex: 1,
                    }}
                />
            )}

            {/* 상단 유의사항 */}
            <Box
                sx={{
                    backgroundColor: '#F7921E',
                    color: 'white',
                    padding: 2,
                    borderRadius: '16px',
                    textAlign: 'center',
                    marginBottom: 3, 
                    width: '100%',
                    maxWidth: '375px',
                    margin: '0 auto 24px auto',
                    position: 'relative',
                    zIndex: 10, // 로고 위에 표시되도록 z-index 설정
                }}
            >
                <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: 0.5 }}>
                    메시지 작성 시 유의사항
                </Typography>
                <Typography variant="body2">
                    {workspaceSettings.noticeMessage?.split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                            {line}<br />
                        </React.Fragment>
                    ))}
                </Typography>
            </Box>

            {/* 메시지 작성 영역 */}
            <Box
                sx={{
                    background: 'linear-gradient(to top, #fff, #f8f8f8)',
                    width: '100%',
                    maxWidth: '375px',
                    minWidth: '0',
                    margin: '0 auto',
                    padding: 3,
                    borderRadius: '16px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography variant="h6" textAlign="center" marginBottom={2} color="gray">
                    메시지를 남겨주세요
                </Typography>
                <TextField
                    multiline
                    fullWidth
                    minRows={6}
                    placeholder="메시지를 작성하세요"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    variant="outlined"
                    sx={{
                        background: 'white',
                        borderRadius: '8px',
                        marginBottom: 2,
                        width: '100%',
                    }}
                    inputProps={{
                        maxLength: 100,
                    }}
                />
                <Typography variant="caption" color="gray" display="block" textAlign="center" marginBottom={2}>
                    [{message.length}/100]
                </Typography>

                {/* 성명, 소속, 직책, 전화번호 입력 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        marginBottom: 2,
                        width: '100%',
                    }}
                >
                    <TextField
                        fullWidth
                        placeholder="성명"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        variant="outlined"
                        sx={{ background: 'white', borderRadius: '8px', width: '100%' }}
                    />
                    <TextField
                        fullWidth
                        placeholder="소속(선택)"
                        value={affiliation}
                        onChange={(e) => setAffiliation(e.target.value)}
                        variant="outlined"
                        sx={{ background: 'white', borderRadius: '8px', width: '100%' }}
                    />
                    <TextField
                        fullWidth
                        placeholder="직책(선택)"
                        value={position}
                        onChange={(e) => setPosition(e.target.value)}
                        variant="outlined"
                        sx={{ background: 'white', borderRadius: '8px', width: '100%' }}
                    />
                    <TextField
                        fullWidth
                        placeholder="전화번호(선택) 예: 010-1234-5678"
                        value={phone}
                        onChange={handlePhoneChange}
                        variant="outlined"
                        sx={{ background: 'white', borderRadius: '8px', width: '100%' }}
                    />
                </Box>

                {/* 사진 업로드 */}
                <Button
                    variant="contained"
                    component="label"
                    fullWidth
                    sx={{
                        textTransform: 'none',
                        marginBottom: 2,
                        borderRadius: '8px',
                        backgroundColor: '#46066A',
                        '&:hover': {
                            backgroundColor: '#5a0888'
                        }
                    }}
                >
                    사진 추가(선택)
                    <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                </Button>

                {/* 카드 미리보기 */}
                <Card
                    sx={{
                        marginBottom: 3,
                        padding: 2, // 실제 카드와 동일한 padding
                        borderRadius: '16px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        overflow: 'hidden',
                        background: `url('/images/bg_card1.png'), #fdf6e3`, // 별 배경 이미지 적용
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        color: '#000000', // 실제 카드와 동일한 텍스트 색상
                        display: 'flex',
                        flexDirection: 'column',
                        width: '300px', // 실제 카드와 동일한 고정 너비
                        height: '300px', // 실제 카드와 동일한 고정 높이
                        margin: '0 auto', // 중앙 정렬
                        flexShrink: 0, // 크기 고정
                    }}
                >
                    {photoPreview && (
                        <Box
                            component="img"
                            src={photoPreview}
                            alt="Photo Preview"
                            sx={{
                                width: '100%',
                                height: '150px', // 실제 카드와 동일한 높이
                                objectFit: 'contain',
                                borderTopLeftRadius: '16px',
                                borderTopRightRadius: '16px',
                            }}
                        />
                    )}
                    <CardContent
                        sx={{
                            flex: 1, // 남은 공간을 차지하도록 설정
                            display: photoPreview ? 'block' : 'flex', // 이미지가 있을 경우 기본 블록 정렬
                            flexDirection: photoPreview ? 'initial' : 'column', // 이미지는 세로 정렬 제외
                            alignItems: photoPreview ? 'initial' : 'center', // 이미지 없을 때 가로 중앙 정렬
                            justifyContent: photoPreview ? 'initial' : 'center', // 이미지 없을 때 세로 중앙 정렬
                            height: '100%', // CardContent 전체 높이 사용
                            textAlign: 'center', // 텍스트 중앙 정렬
                            padding: 0, // 실제 카드와 동일한 padding
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontFamily: 'GmarketSansMedium', // 실제 카드와 동일한 폰트
                                fontSize: '20px', // 실제 카드와 동일한 폰트 크기
                                whiteSpace: 'pre-line', // 줄바꿈 반영
                                textAlign: 'center', // 텍스트 중앙 정렬
                                wordBreak: 'break-word', // 긴 단어 줄바꿈
                                maxHeight: 'unset', // 높이 제한 제거
                                alignItems: 'center',
                            }}
                        >
                            {message || '메시지 미리보기가 표시됩니다.'}
                        </Typography>
                        {name ? (
                            <Typography
                                variant="caption"
                                sx={{
                                    margin: 0,
                                    marginTop: 0,
                                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                    fontWeight: 400,
                                    fontSize: '0.75rem',
                                    lineHeight: 1.66,
                                    letterSpacing: '0.03333em',
                                }}
                            >
                                {name} · {affiliation || '소속 없음'} · {position || '직책 없음'}
                            </Typography>
                        ) : (
                            <Typography
                                variant="caption"
                                sx={{
                                    margin: 0,
                                    marginTop: 0,
                                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                    fontWeight: 400,
                                    fontSize: '0.75rem',
                                    lineHeight: 1.66,
                                    letterSpacing: '0.03333em',
                                }}
                            >
                                · 소속 없음 · 직책 없음
                            </Typography>
                        )}
                    </CardContent>
                </Card>


                {/* 개인정보 동의 */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isAgreed}
                            onChange={(e) => setIsAgreed(e.target.checked)}
                            sx={{ color: 'primary.main' }}
                        />
                    }
                    label={
                        <Typography variant="caption" color="gray"
                                    onClick={handleDialogOpen}
                        >
                            [필수] 개인정보 수집 및 이용 동의
                        </Typography>
                    }
                />
            </Box>

            {/* 완료 버튼 */}
            <Button
                variant="contained"
                onClick={handleSaveClick}
                component="label"
                fullWidth
                disabled={!workspaceSettings.isMessageBoardOpen} // 메시지보드가 닫힌 경우 버튼 비활성화
                sx={{
                    marginTop: 2,
                    textTransform: 'none',
                    marginBottom: 0,
                    borderRadius: '8px',
                    backgroundColor: '#46066A',
                    maxWidth: '375px',
                    margin: '16px auto 0 auto',
                        '&:hover': {
                            backgroundColor: '#5a0888'
                        }
                }}
            >
                {loading ? '등록 중...' : '메시지 저장'}
            </Button>

            {loading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)', // 반투명 배경
                        zIndex: 1000,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            {/* 일반 알림 다이얼로그 */}
            <Dialog open={isAlertDialogOpen} onClose={handleAlertDialogClose}>
                <DialogContent
                    sx={{
                        padding: 3,
                        borderRadius: '16px',
                        backgroundColor: 'white',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="body1" sx={{ marginBottom: 2 }}>
                        {alertMessage}
                    </Typography>
                    <Button 
                        variant="contained" 
                        onClick={handleAlertDialogClose}
                        sx={{ 
                            backgroundColor: '#46066A',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#5a0888',
                            }
                        }}
                    >
                        확인
                    </Button>
                </DialogContent>
            </Dialog>

            {/* 메시지 거부 다이얼로그 */}
            <Dialog open={isMessageRejectDialogOpen} onClose={handleMessageRejectDialogClose}>
                <DialogContent
                    sx={{
                        padding: 3,
                        borderRadius: '16px',
                        backgroundColor: 'white',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 2, color: 'error.main' }}>
                        메시지 수정이 필요합니다
                    </Typography>
                    <Typography variant="body1" sx={{ marginBottom: 1 }}>
                        {rejectReason.explanation || '부적절한 내용이 포함되어 있습니다.'}
                    </Typography>
                    <Typography variant="body1" sx={{ marginBottom: 3, fontWeight: 'bold' }}>
                        메시지를 다시 작성해주세요.
                    </Typography>
                    <Button 
                        variant="contained" 
                        onClick={handleMessageRejectDialogClose}
                        sx={{ 
                            backgroundColor: 'error.main',
                            '&:hover': {
                                backgroundColor: 'error.dark',
                            }
                        }}
                    >
                        확인
                    </Button>
                </DialogContent>
            </Dialog>

            {/* 개인정보 동의 팝업 */}
            <Dialog open={isDialogOpen} onClose={handleDialogClose}>
                <DialogContent
                    sx={{
                        padding: 3,
                        borderRadius: '16px',
                        backgroundColor: 'white',
                        textAlign: 'left',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 2 }}>
                        개인정보 수집·이용 동의
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: 1, whiteSpace: 'pre-line' }}>
                        {workspaceSettings.privacyAgreement || '개인정보 수집·이용 동의 내용이 없습니다.'}
                    </Typography>
                    <Button variant="contained" onClick={handleDialogClose} sx={{ marginTop: 2,
                        backgroundColor: '#46066A',
                        '&:hover': {
                            backgroundColor: '#5a0888'
                        }
                    }}>
                        닫기
                    </Button>
                </DialogContent>
            </Dialog>

            {/* 확인 다이얼로그 */}
            <Dialog open={isConfirmDialogOpen} onClose={handleCancel}>
                <DialogContent
                    sx={{
                        padding: 3,
                        borderRadius: '16px',
                        backgroundColor: 'white',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: 2 }}>
                        작성을 완료하시겠습니까?
                    </Typography>
                    <Box
                        sx={{
                            background: '#F7F8FA',
                            borderRadius: '16px',
                            padding: 2,
                            marginBottom: 2,
                            textAlign: 'left',
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                            다시 한 번 확인해 주세요!
                        </Typography>
                        <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                            • 욕설이나 비방은 자제해 주세요.
                        </Typography>
                        <Typography variant="body2">
                            • 메시지 내용은 완료 후 수정이 불가합니다.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            onClick={handleConfirm}
                            sx={{
                                backgroundColor: '#46066A',
                                '&:hover': { backgroundColor: '#5a0888' }
                            }}
                        >
                            확인
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleCancel}
                        >
                            취소
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

        </Box>
    );
};

export default Guestbook;
